// Role selection page - first step of onboarding
// Users select their role: Property Manager/Owner or Leaseholder

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui';
import { apiClient } from '../../services/api';

type SelectedRole = 'owner' | 'manager' | 'leaseholder' | null;

export function RoleSelectionPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState<SelectedRole>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRoleSelect = (role: SelectedRole) => {
    setSelectedRole(role);
    setError(null);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      setError('Please select a role to continue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post('/onboarding/role', { role: selectedRole });
      await refreshUser();

      if (selectedRole === 'leaseholder') {
        // Leaseholders who haven't been assigned go to waiting page
        navigate('/onboarding/waiting');
      } else {
        // Owners/Managers proceed to organization choice (create vs join)
        navigate('/onboarding/organization-choice');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Welcome</h1>
          <p className="mt-2 text-gray-600">
            Hi {user?.name || 'there'}! Let's get you set up.
          </p>
        </div>

        {/* Role selection */}
        <div className="card p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            What best describes you?
          </h2>
          <p className="text-gray-600 mb-6">
            This helps us personalize your experience.
          </p>

          {error && (
            <div
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Owner/Manager option */}
            <button
              type="button"
              onClick={() => handleRoleSelect('owner')}
              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                selectedRole === 'owner' || selectedRole === 'manager'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    Property Manager or Owner
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    I manage properties, handle maintenance issues, and work with tenants or leaseholders.
                  </p>
                </div>
                {(selectedRole === 'owner' || selectedRole === 'manager') && (
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>

            {/* Leaseholder option */}
            <button
              type="button"
              onClick={() => handleRoleSelect('leaseholder')}
              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                selectedRole === 'leaseholder'
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Leaseholder</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    I rent or lease a property and need to report maintenance issues to my property manager.
                  </p>
                </div>
                {selectedRole === 'leaseholder' && (
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-primary-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          </div>

          <div className="mt-8">
            <Button
              onClick={handleContinue}
              className="w-full"
              disabled={!selectedRole || isSubmitting}
              isLoading={isSubmitting}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleSelectionPage;
